import axios from 'axios';
import { useCallback } from 'react';
import { calculatePrice, getPerWordRate } from '../../../config/priceConfig';

/**
 * Custom hook to encapsulate polling and request callbacks for Upload.jsx
 * @param {object} params - All required state, refs, and setters from Upload.jsx
 * @returns {object} - The callback functions
 */
export default function useUploadCallbacks({
  ENV,
  HOST,
  setRateTask,
  setEstimate,
  setViewState,
  setLoadingEstimate,
  setTranslationTask,
  setTranslatedFile,
  setTranslationProgress,
  setTranslationStatus,
  setLoadingTranslate,
  toast,
  maxRetries,
  MIN_POLL_INTERVAL,
  MAX_POLL_INTERVAL,
  ratePollingRef,
  translatePollingRef,
  retryCountRef,
  isPollingRef,
  lastRequestTimeRef,
  requestInFlightRef,
  selectedType,
  setCreditEstimate,
}) {
  // Enhanced cleanup function
  const clearPolling = useCallback(() => {
    console.log(' Clearing all polling intervals');

    if (ratePollingRef.current) {
      clearInterval(ratePollingRef.current);
      ratePollingRef.current = null;
    }
    if (translatePollingRef.current) {
      clearInterval(translatePollingRef.current);
      translatePollingRef.current = null;
    }

    isPollingRef.current = false;
    requestInFlightRef.current = false;
    retryCountRef.current = 0;
  }, [
    ratePollingRef,
    translatePollingRef,
    isPollingRef,
    requestInFlightRef,
    retryCountRef,
  ]);

  // Rate-limited request function
  const makeRequest = useCallback(
    async (url, options = {}) => {
      const now = Date.now();
      const timeSinceLastRequest = now - lastRequestTimeRef.current;

      // Prevent requests too close together
      if (timeSinceLastRequest < 2000) {
        console.log(' Request rate limited, waiting...');
        await new Promise((resolve) =>
          setTimeout(resolve, 2000 - timeSinceLastRequest)
        );
      }

      // Prevent concurrent requests
      if (requestInFlightRef.current) {
        console.log(' Request already in flight, skipping...');
        throw new Error('Request already in progress');
      }

      requestInFlightRef.current = true;
      lastRequestTimeRef.current = Date.now();

      let timeoutDuration = 20000; // Default timeout of 20 seconds

      if (url.includes('/translate') || url.includes('/rate')) {
        // For translate or rate endpoints, use a longer timeout
        timeoutDuration = ENV === 'local' ? 300000 : 45000; // 5 mins for dev, 45s for prod
      }

      try {
        const controller = new AbortController();
        const timeoutId = setTimeout(() => {
          console.log(' Request timeout, aborting...');
          controller.abort();
        }, timeoutDuration); // Increased timeout

        const response = await fetch(url, {
          ...options,
          signal: controller.signal,
          headers: {
            'Cache-Control': 'no-cache, no-store, must-revalidate',
            Pragma: 'no-cache',
            Expires: '0',
            ...options.headers,
          },
        });

        clearTimeout(timeoutId);
        return response;
      } finally {
        requestInFlightRef.current = false;
      }
    },
    [ENV, lastRequestTimeRef, requestInFlightRef]
  );

  // Optimized polling function with exponential backoff
  const pollTaskStatus = useCallback(
    async (taskId, taskType) => {
      if (!isPollingRef.current) {
        console.log(' Polling stopped, exiting...');
        return;
      }

      try {
        console.log(
          ` Polling ${taskType} task: ${taskId} (attempt ${retryCountRef.current + 1})`
        );

        const response = await makeRequest(`${HOST}/task/${taskId}`);

        if (!response.ok) {
          throw new Error(`HTTP ${response.status}: ${response.statusText}`);
        }

        const data = await response.json();
        console.log(` Task status: ${data.status}`, data);

        retryCountRef.current = 0; // Reset retry count on success

        // Handle rate calculation responses
        if (taskType === 'rate') {
          if (data.status === 'SUCCESS' || data.status === 'COMPLETED') {
            console.log('Rate calculation completed');

            // Validate the result before proceeding
            if (!data.result || typeof data.result.word_count === 'undefined') {
              toast.error('Invalid response from server.');
              setLoadingEstimate(false);
              clearPolling();
              setRateTask({ ...data, completed: true, failed: true });
              return;
            }

            const wordCount = Number(data.result.word_count);
            const per_word_rate = Number(getPerWordRate(selectedType));
            const amount = Number(calculatePrice(wordCount, selectedType));

            setRateTask({ ...data, completed: true });
            setEstimate({
              word_count: wordCount,
              per_word_rate,
              amount_to_pay: amount,
              estimated_time: data.result.estimated_time,
            });

            // Call backend to check/update credit using sessionStorage user_id
            try {
              const user_id = parseInt(sessionStorage.getItem('user_id'));
              const doc_id = sessionStorage.getItem('doc_id');

              if (!user_id) {
                console.error('user_id not available in sessionStorage');
                throw new Error('User ID not available');
              }

              // Get email from sessionStorage user object
              const userData = sessionStorage.getItem('user');
              const email = userData ? JSON.parse(userData).email : '';

              const backendRes = await axios.post(
                `${process.env.REACT_APP_DB_SERVER_URL}/credit/estimate`,
                { user_id, cost: amount, wordcount: wordCount, email, doc_id: doc_id ? parseInt(doc_id) : null }
              );
              setCreditEstimate(backendRes.data);
            } catch (creditError) {
              console.error('Credit estimation error:', creditError);
              // Continue with rate calculation even if credit call fails
            }

            setViewState('estimate');
            setLoadingEstimate(false);
            clearPolling();
            return;
          }

          if (data.status === 'FAILED' || data.status === 'FAILURE') {
            console.log('Rate calculation failed');
            setRateTask({ ...data, completed: true, failed: true });
            setLoadingEstimate(false);
            clearPolling();
            toast.error(
              `Rate calculation failed: ${data.error || 'Unknown error'}`
            );
            return;
          }

          // Still processing: continue polling
          setRateTask(data);
        }
        // Handle translation responses
        else if (taskType === 'translate') {
          if (data.status === 'SUCCESS' || data.status === 'COMPLETED') {
            console.log(' Translation completed');
            setTranslationTask({ ...data, completed: true });
            setTranslatedFile({
              pdfPath: data.result.pdf_path,
              docxPath: data.result.docx_path,
              textPath: data.result.text_path,
            });
            setViewState('result');
            setTranslationProgress(100);
            setTranslationStatus('Completed');
            setLoadingTranslate(false);
            clearPolling(); // Stop all polling
            toast.success('Translation completed successfully');
            return; // Exit polling
          } else if (data.status === 'FAILED' || data.status === 'FAILURE') {
            console.log(' Translation failed');
            setTranslationTask({ ...data, completed: true, failed: true });
            setLoadingTranslate(false);
            setTranslationStatus('Failed');
            clearPolling();
            toast.error(`Translation failed: ${data.error || 'Unknown error'}`);
            return; // Exit polling
          } else {
            setTranslationTask(data);

            // Update progress with more granular steps
            const progressMap = {
              PENDING: 10,
              PROCESSING: 25,
              TRANSLATING: 50,
              SAVING: 75,
              GENERATING: 90,
            };

            const progress = progressMap[data.status] || 25;
            setTranslationProgress(progress);
            setTranslationStatus(data.status.toLowerCase().replace('_', ' '));
          }
        }
      } catch (error) {
        console.error(' Polling error:', error);
        retryCountRef.current += 1;

        // Stop polling if max retries reached
        if (retryCountRef.current >= maxRetries) {
          console.error(
            ` Max retries (${maxRetries}) reached for ${taskType} task ${taskId}`
          );

          if (taskType === 'rate') {
            setLoadingEstimate(false);
            toast.error('Rate calculation timed out. Please try again.');
          } else if (taskType === 'translate') {
            setLoadingTranslate(false);
            setTranslationStatus('Connection lost');
            toast.error(
              'Translation monitoring lost. The task may still be running. Please check back later.'
            );
          }

          clearPolling();
          return; // Exit polling
        } else {
          console.log(
            ` Will retry in next polling cycle (${retryCountRef.current}/${maxRetries})`
          );
        }
      }
    },
    [
      HOST,
      makeRequest,
      clearPolling,
      setRateTask,
      setEstimate,
      setViewState,
      setLoadingEstimate,
      setTranslationTask,
      setTranslatedFile,
      setTranslationProgress,
      setTranslationStatus,
      setLoadingTranslate,
      toast,
      maxRetries,
      isPollingRef,
      retryCountRef,
      selectedType,
      setCreditEstimate,
    ]
  );

  // Optimized polling starter with adaptive intervals and dev mode handling
  const startPolling = useCallback(
    (taskId, taskType, initialStatus = 'PENDING') => {
      console.log(
        ` Starting ${taskType} polling for task: ${taskId}, initialStatus: ${initialStatus}`
      );

      // Skip polling if task is already completed (dev mode)
      if (initialStatus === 'COMPLETED') {
        console.log(' Task already completed (dev mode), skipping polling');
        return;
      }

      // Clear any existing polling
      clearPolling();

      // Set polling flag
      isPollingRef.current = true;
      retryCountRef.current = 0;

      // Calculate adaptive interval based on task type
      let pollInterval = taskType === 'rate' ? 15000 : MIN_POLL_INTERVAL; // Rate: 10s, Translate: 8s

      // Immediate first call
      pollTaskStatus(taskId, taskType);

      // Set up interval with exponential backoff on errors
      const intervalId = setInterval(() => {
        if (!isPollingRef.current) {
          console.log(' Polling flag is false, stopping interval');
          clearInterval(intervalId);
          return;
        }

        // Increase interval if there are retries (exponential backoff)
        if (retryCountRef.current > 0) {
          const backoffMultiplier = Math.pow(2, retryCountRef.current);
          pollInterval = Math.min(
            pollInterval * backoffMultiplier,
            MAX_POLL_INTERVAL
          );
          console.log(
            ` Increased poll interval to ${pollInterval}ms due to errors`
          );
        }

        pollTaskStatus(taskId, taskType);
      }, pollInterval);

      // Store interval reference
      if (taskType === 'rate') {
        ratePollingRef.current = intervalId;
      } else if (taskType === 'translate') {
        translatePollingRef.current = intervalId;
      }

      // Auto-cleanup after maximum time (30 minutes)
      setTimeout(
        () => {
          if (isPollingRef.current) {
            console.log(' Auto-stopping polling after 30 minutes');
            clearPolling();
           
          }
        },
        30 * 60 * 1000
      ); // 30 minutes
    },
    [
      pollTaskStatus,
      clearPolling,
      isPollingRef,
      retryCountRef,
      MIN_POLL_INTERVAL,
      MAX_POLL_INTERVAL,
      toast,
      ratePollingRef,
      translatePollingRef,
    ]
  );

  return { clearPolling, makeRequest, pollTaskStatus, startPolling };
}
